import { Injectable } from '@angular/core';
import { ERROR_CODES, QUESTIONNAIRE_STATUS } from '../constants/fhir.constants';
import { ConversionError, IFhirValidator } from '../interfaces/fhir-converter.interfaces';
import { FHIRItem } from '../models/fhir.model';

/** FHIR R4 item types that support answerValueSet and answerOption (que-5). */
const ANSWERABLE_TYPES = new Set([
  'choice',
  'open-choice',
  'decimal',
  'integer',
  'date',
  'dateTime',
  'time',
  'string',
  'quantity',
]);

/** FHIR R4 item types that support maxLength (que-10). */
const MAXLENGTH_TYPES = new Set(['boolean', 'decimal', 'integer', 'string', 'text', 'url', 'open-choice']);

/** All valid FHIR R4 Questionnaire item types. */
const VALID_ITEM_TYPES = new Set([
  'group',
  'display',
  'boolean',
  'decimal',
  'integer',
  'date',
  'dateTime',
  'time',
  'string',
  'text',
  'url',
  'choice',
  'open-choice',
  'attachment',
  'reference',
  'quantity',
]);

/**
 * FHIR R4 Questionnaire Validator Service
 *
 * Implements all FHIR R4 Questionnaire invariants (que-0 through que-13):
 *
 * - **que-0** (Warning): Name should be usable as an identifier
 * - **que-1** (Rule): Group items must have nested items; display items cannot have nested items
 * - **que-2** (Rule): Link IDs must be unique across the entire questionnaire
 * - **que-3** (Rule): Display items cannot have a "code" asserted
 * - **que-4** (Rule): A question cannot have both answerOption and answerValueSet
 * - **que-5** (Rule): Only answerable types can have answerValueSet/answerOption
 * - **que-6** (Rule): Required and repeats aren't permitted for display items
 * - **que-7** (Rule): If the operator is 'exists', the value must be a boolean
 * - **que-8** (Rule): Initial values can't be specified for groups or display items
 * - **que-9** (Rule): readOnly can't be specified for display items
 * - **que-10** (Rule): maxLength can only be declared for simple question types
 * - **que-11** (Rule): answerOption and initial are mutually exclusive
 * - **que-12** (Rule): Multiple enableWhen conditions require enableBehavior
 * - **que-13** (Rule): Multiple initial values require repeats = true
 */
@Injectable({
  providedIn: 'root',
})
export class FhirValidator implements IFhirValidator {
  /** Valid FHIR Questionnaire status values */
  private readonly validStatuses = Object.values(QUESTIONNAIRE_STATUS);

  /**
   * Validate a FHIR Questionnaire resource
   *
   * @param questionnaire - Object to validate
   * @param errors - Array to collect errors (optional)
   * @param warnings - Array to collect warnings (optional)
   * @returns True if valid, false if has errors
   */
  validate(questionnaire: unknown, errors: ConversionError[] = [], warnings: string[] = []): boolean {
    const q = questionnaire as Record<string, unknown>;

    // Validate resource type
    this.validateResourceType(q, errors);

    // Validate status
    this.validateStatus(q, errors);

    // que-0: Name should be usable as an identifier
    this.validateName(q, warnings);

    // Validate items
    const items = q['item'] as FHIRItem[] | undefined;
    if (items && Array.isArray(items)) {
      // que-2: Collect all linkIds across entire questionnaire for global uniqueness
      const allLinkIds = new Set<string>();
      this.collectAllLinkIds(items, allLinkIds, errors);

      // Validate each item recursively
      this.validateItems(items, errors, warnings, '');
    }

    return !errors.some((e) => e.severity === 'error');
  }

  /**
   * Validate FHIR items recursively with all R4 constraints
   *
   * @param items - Array of items to validate
   * @param errors - Array to collect errors
   * @param warnings - Array to collect warnings
   * @param parentPath - Parent path for error reporting
   */
  validateItems(items: FHIRItem[], errors: ConversionError[], warnings: string[], parentPath: string): void {
    for (const item of items) {
      const path = parentPath ? `${parentPath}.item(${item.linkId})` : `item(${item.linkId})`;

      // Validate required fields (linkId, type)
      this.validateRequiredFields(item, path, errors);

      // Validate item type is valid
      this.validateItemType_R4(item, path, errors);

      // que-1: Group items must have nested items; display items cannot have nested items
      this.validateQue1(item, path, errors);

      // que-3: Display items cannot have a "code" asserted
      this.validateQue3(item, path, errors);

      // que-4: A question cannot have both answerOption and answerValueSet
      this.validateQue4(item, path, errors);

      // que-5: Only answerable types can have answerValueSet/answerOption
      this.validateQue5(item, path, errors);

      // que-6: Required and repeats aren't permitted for display items
      this.validateQue6(item, path, errors);

      // que-7: If the operator is 'exists', the value must be a boolean
      this.validateQue7(item, path, errors);

      // que-8: Initial values can't be specified for groups or display items
      this.validateQue8(item, path, errors);

      // que-9: readOnly can't be specified for display items
      this.validateQue9(item, path, errors);

      // que-10: maxLength can only be declared for simple question types
      this.validateQue10(item, path, errors);

      // que-11: answerOption and initial are mutually exclusive
      this.validateQue11(item, path, errors);

      // que-12: Multiple enableWhen conditions require enableBehavior
      this.validateQue12(item, path, errors);

      // que-13: Multiple initial values require repeats = true
      this.validateQue13(item, path, errors);

      // Validate answer options for choice types (warning)
      this.validateAnswerOptions(item, path, warnings);

      // Validate nested items recursively
      if (item.item && Array.isArray(item.item)) {
        this.validateItems(item.item, errors, warnings, path);
      }
    }
  }

  /** Validate item type is valid FHIR type */
  validateItemType(type: string): boolean {
    return VALID_ITEM_TYPES.has(type);
  }

  /** Validate linkId format — linkIds should be alphanumeric with dots, dashes, and underscores */
  validateLinkIdFormat(linkId: string): boolean {
    const pattern = /^[a-zA-Z0-9._-]+$/;
    return pattern.test(linkId);
  }

  // ------------------------------------------------------------------
  // Private: Resource-level validations
  // ------------------------------------------------------------------

  /** Validate that resource type is Questionnaire */
  private validateResourceType(questionnaire: Record<string, unknown>, errors: ConversionError[]): void {
    if (questionnaire['resourceType'] !== 'Questionnaire') {
      errors.push({
        code: ERROR_CODES.INVALID_RESOURCE_TYPE,
        message: 'resourceType must be "Questionnaire"',
        severity: 'error',
      });
    }
  }

  /** Validate questionnaire status */
  private validateStatus(questionnaire: Record<string, unknown>, errors: ConversionError[]): void {
    const status = questionnaire['status'];

    if (!status) {
      errors.push({
        code: ERROR_CODES.MISSING_STATUS,
        message: 'status is required',
        severity: 'error',
      });
      return;
    }

    if (!this.validStatuses.includes(status as (typeof this.validStatuses)[number])) {
      errors.push({
        code: ERROR_CODES.INVALID_STATUS,
        message: `status must be one of: ${this.validStatuses.join(', ')}`,
        severity: 'error',
      });
    }
  }

  /** Que-0: Name should be usable as an identifier for machine processing */
  private validateName(questionnaire: Record<string, unknown>, warnings: string[]): void {
    const name = questionnaire['name'] as string | undefined;
    if (name && !/^[A-Z]([A-Za-z0-9_]){0,254}$/.test(name)) {
      warnings.push(
        `que-0: Name "${name}" should match pattern [A-Z]([A-Za-z0-9_]){0,254} to be usable as an identifier`
      );
    }
  }

  // ------------------------------------------------------------------
  // Private: que-2 — Global linkId uniqueness
  // ------------------------------------------------------------------

  /** Collect all linkIds across the entire questionnaire tree and report duplicates */
  private collectAllLinkIds(items: FHIRItem[], allLinkIds: Set<string>, errors: ConversionError[]): void {
    for (const item of items) {
      if (item.linkId) {
        if (allLinkIds.has(item.linkId)) {
          errors.push({
            code: ERROR_CODES.DUPLICATE_LINKID,
            message: `que-2: Duplicate linkId "${item.linkId}" — link IDs must be unique across the entire questionnaire`,
            path: item.linkId,
            severity: 'error',
          });
        }
        allLinkIds.add(item.linkId);
      }
      if (item.item && Array.isArray(item.item)) {
        this.collectAllLinkIds(item.item, allLinkIds, errors);
      }
    }
  }

  // ------------------------------------------------------------------
  // Private: Item-level validations
  // ------------------------------------------------------------------

  /** Validate required item fields */
  private validateRequiredFields(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (!item.linkId) {
      errors.push({
        code: ERROR_CODES.MISSING_LINKID,
        message: 'Item is missing required linkId',
        path,
        severity: 'error',
      });
    }

    if (!item.type) {
      errors.push({
        code: ERROR_CODES.MISSING_TYPE,
        message: 'Item is missing required type',
        path,
        severity: 'error',
      });
    }
  }

  /** Validate item type is a valid FHIR R4 type */
  private validateItemType_R4(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.type && !VALID_ITEM_TYPES.has(item.type)) {
      errors.push({
        code: ERROR_CODES.INVALID_ITEM_TYPE,
        message: `Invalid item type "${item.type}". Must be one of: ${[...VALID_ITEM_TYPES].join(', ')}`,
        path,
        severity: 'error',
      });
    }
  }

  /** Que-1: Group items must have nested items; display items cannot have nested items */
  private validateQue1(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.type === 'group') {
      if (!item.item || item.item.length === 0) {
        errors.push({
          code: ERROR_CODES.QUE_1_GROUP_REQUIRES_ITEMS,
          message: 'que-1: Group items must have nested items',
          path,
          severity: 'error',
        });
      }
    }
    if (item.type === 'display') {
      if (item.item && item.item.length > 0) {
        errors.push({
          code: ERROR_CODES.QUE_1_DISPLAY_NO_ITEMS,
          message: 'que-1: Display items cannot have nested items',
          path,
          severity: 'error',
        });
      }
    }
  }

  /** Que-3: Display items cannot have a "code" asserted */
  private validateQue3(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.type === 'display' && item.code && item.code.length > 0) {
      errors.push({
        code: ERROR_CODES.QUE_3_DISPLAY_NO_CODE,
        message: 'que-3: Display items cannot have a "code" asserted',
        path,
        severity: 'error',
      });
    }
  }

  /** Que-4: A question cannot have both answerOption and answerValueSet */
  private validateQue4(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (
      item.answerOption &&
      item.answerOption.length > 0 &&
      item.answerValueSet !== undefined &&
      item.answerValueSet !== null &&
      item.answerValueSet !== ''
    ) {
      errors.push({
        code: ERROR_CODES.QUE_4_OPTION_VS_VALUESET,
        message: 'que-4: A question cannot have both answerOption and answerValueSet',
        path,
        severity: 'error',
      });
    }
  }

  /** Que-5: Only answerable types can have answerValueSet/answerOption */
  private validateQue5(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (!item.type || ANSWERABLE_TYPES.has(item.type)) {
      return;
    }
    const hasAnswerValueSet =
      item.answerValueSet !== undefined && item.answerValueSet !== null && item.answerValueSet !== '';
    const hasAnswerOption = item.answerOption !== undefined && item.answerOption.length > 0;
    if (hasAnswerValueSet || hasAnswerOption) {
      errors.push({
        code: ERROR_CODES.QUE_5_INVALID_ANSWER_TYPE,
        message: `que-5: Items of type "${item.type}" cannot have answerValueSet or answerOption`,
        path,
        severity: 'error',
      });
    }
  }

  /** Que-6: Required and repeats aren't permitted for display items */
  private validateQue6(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.type === 'display') {
      if (item.required !== undefined) {
        errors.push({
          code: ERROR_CODES.QUE_6_DISPLAY_NO_REQUIRED_REPEATS,
          message: 'que-6: "required" is not permitted for display items',
          path,
          severity: 'error',
        });
      }
      if (item.repeats !== undefined) {
        errors.push({
          code: ERROR_CODES.QUE_6_DISPLAY_NO_REQUIRED_REPEATS,
          message: 'que-6: "repeats" is not permitted for display items',
          path,
          severity: 'error',
        });
      }
    }
  }

  /** Que-7: If the operator is 'exists', the value must be a boolean */
  private validateQue7(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (!item.enableWhen) {
      return;
    }
    for (let i = 0; i < item.enableWhen.length; i++) {
      const ew = item.enableWhen[i];
      if (ew.operator === 'exists') {
        // Only answerBoolean should be set
        if (ew.answerBoolean === undefined) {
          errors.push({
            code: ERROR_CODES.QUE_7_EXISTS_REQUIRES_BOOLEAN,
            message: `que-7: enableWhen[${i}] with operator "exists" must have a boolean answer value`,
            path: `${path}.enableWhen[${i}]`,
            severity: 'error',
          });
        }
      }
    }
  }

  /** Que-8: Initial values can't be specified for groups or display items */
  private validateQue8(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if ((item.type === 'group' || item.type === 'display') && item.initial && item.initial.length > 0) {
      errors.push({
        code: ERROR_CODES.QUE_8_NO_INITIAL_FOR_GROUP_DISPLAY,
        message: `que-8: Initial values cannot be specified for "${item.type}" items`,
        path,
        severity: 'error',
      });
    }
  }

  /** Que-9: readOnly can't be specified for display items */
  private validateQue9(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.type === 'display' && item.readOnly !== undefined) {
      errors.push({
        code: ERROR_CODES.QUE_9_DISPLAY_NO_READONLY,
        message: 'que-9: "readOnly" cannot be specified for display items',
        path,
        severity: 'error',
      });
    }
  }

  /** Que-10: maxLength can only be declared for simple question types */
  private validateQue10(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.maxLength !== undefined && item.type && !MAXLENGTH_TYPES.has(item.type)) {
      errors.push({
        code: ERROR_CODES.QUE_10_MAXLENGTH_SIMPLE_TYPES,
        message: `que-10: maxLength can only be declared for simple types (${[...MAXLENGTH_TYPES].join(', ')}), not "${item.type}"`,
        path,
        severity: 'error',
      });
    }
  }

  /** Que-11: If one or more answerOption is present, initial must be missing */
  private validateQue11(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.answerOption && item.answerOption.length > 0 && item.initial && item.initial.length > 0) {
      errors.push({
        code: ERROR_CODES.QUE_11_OPTION_VS_INITIAL,
        message: 'que-11: If answerOption is present, initial values must not be specified',
        path,
        severity: 'error',
      });
    }
  }

  /** Que-12: If there are more than one enableWhen, enableBehavior must be specified */
  private validateQue12(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.enableWhen && item.enableWhen.length > 1 && !item.enableBehavior) {
      errors.push({
        code: ERROR_CODES.QUE_12_ENABLE_BEHAVIOR_REQUIRED,
        message: 'que-12: enableBehavior must be specified when there are multiple enableWhen conditions',
        path,
        severity: 'error',
      });
    }
  }

  /** Que-13: Can only have multiple initial values for repeating items */
  private validateQue13(item: FHIRItem, path: string, errors: ConversionError[]): void {
    if (item.initial && item.initial.length > 1 && item.repeats !== true) {
      errors.push({
        code: ERROR_CODES.QUE_13_MULTIPLE_INITIAL_REPEATS,
        message: 'que-13: Multiple initial values can only be specified for items where repeats = true',
        path,
        severity: 'error',
      });
    }
  }

  /** Validate answer options for choice-type items (warning) */
  private validateAnswerOptions(item: FHIRItem, path: string, warnings: string[]): void {
    const choiceTypes = ['choice', 'open-choice'];
    if (choiceTypes.includes(item.type)) {
      const hasAnswerValueSet =
        item.answerValueSet !== undefined && item.answerValueSet !== null && item.answerValueSet !== '';
      if ((!item.answerOption || item.answerOption.length === 0) && !hasAnswerValueSet) {
        warnings.push(`Choice item at ${path} has no answerOption or answerValueSet defined`);
      }
    }
  }
}
